import { FlashList, type ListRenderItem } from '@shopify/flash-list';
import React, { memo, useCallback, useMemo } from 'react';
import { RefreshControl } from 'react-native';

import { imgEmptyActivities } from '@/assets/images';
import { Stack } from '@/components/layout';
import { Image, Skeleton, Text } from '@/components/ui';
import { STRINGS } from '@/constants/strings';
import { createStyles, useScaling } from '@/hooks';
import { colors, guardianColors } from '@/theme/colors';
import FamilyActivityRow from './row';
import type { FamilyActivityItem } from './types';
import { sortFamilyActivityItems } from './utils';

const strings = STRINGS.guardian.home.familyActivity;
const SKELETON_ROWS = 5;

type FamilyActivityListProps = {
  items: FamilyActivityItem[];
  isLoading?: boolean;
  onRefresh?: () => Promise<void> | void;
  isRefreshing?: boolean;
};

function FamilyActivityList({
  items,
  isLoading,
  onRefresh,
  isRefreshing = false,
}: FamilyActivityListProps) {
  const styles = useStyles();
  const { scale } = useScaling();

  const sortedItems = useMemo(() => sortFamilyActivityItems(items), [items]);
  const isInitialLoading = isLoading && sortedItems.length === 0;
  const contentContainerStyle =
    sortedItems.length === 0 && !isInitialLoading ? styles.emptyContent : styles.content;

  const renderItem = useCallback<ListRenderItem<FamilyActivityItem>>(
    ({ item }) => <FamilyActivityRow item={item} />,
    [],
  );

  const keyExtractor = useCallback((item: FamilyActivityItem) => item.id, []);

  const refreshControl = useMemo(
    () =>
      onRefresh ? <RefreshControl refreshing={isRefreshing} onRefresh={onRefresh} /> : undefined,
    [isRefreshing, onRefresh],
  );

  const emptyComponent = useMemo(() => {
    if (isInitialLoading) {
      return (
        <Stack gap={scale(16)}>
          {Array.from({ length: SKELETON_ROWS }).map((_, index) => (
            <Stack
              key={index}
              direction="row"
              align="center"
              gap={scale(8)}
              style={styles.skeletonRow}>
              <Skeleton width={scale(32)} height={scale(32)} borderRadius={scale(16)} />
              <Skeleton width="70%" height={scale(16)} borderRadius={scale(4)} />
            </Stack>
          ))}
        </Stack>
      );
    }

    return (
      <Stack align="center" justify="center" gap={scale(16)} style={styles.empty}>
        <Image
          source={imgEmptyActivities}
          width={scale(188)}
          height={scale(140)}
          resizeMode="contain"
        />
        <Text
          numberOfLines={1}
          variant="Font Guardian App/Subtitle/Subtitle 14 Semibold"
          style={styles.emptyText}>
          {strings.empty}
        </Text>
      </Stack>
    );
  }, [isInitialLoading, scale, styles.empty, styles.emptyText, styles.skeletonRow]);

  return (
    <FlashList
      style={styles.list}
      data={sortedItems}
      renderItem={renderItem}
      keyExtractor={keyExtractor}
      ListEmptyComponent={emptyComponent}
      refreshControl={refreshControl}
      showsVerticalScrollIndicator={false}
      contentContainerStyle={contentContainerStyle}
    />
  );
}

const useStyles = createStyles(({ scale }) => ({
  list: {
    flex: 1,
  },
  content: {
    paddingTop: scale(8),
    paddingBottom: scale(8),
    backgroundColor: colors['0'],
    borderRadius: scale(12),
  },
  emptyContent: {
    flexGrow: 1,
    paddingBottom: scale(8),
  },
  skeletonRow: {
    paddingHorizontal: scale(12),
    paddingVertical: scale(8),
  },
  empty: {
    backgroundColor: colors['0'],
    borderRadius: scale(12),
    height: scale(287),
  },
  emptyText: {
    color: guardianColors['6'],
  },
}));

export default memo(FamilyActivityList);
